/**
 * buildSceneJobInput — the ONE place a Backlog scene candidate becomes a
 * render JobInput (SceneRow's Render / Render-again buttons and the
 * SceneDetailModal's footer all route through SceneRow.handleRender → here).
 *
 * Extracted from SceneRow so the payload shape is unit-testable: for an entry
 * with no boundary adjustment this MUST stay byte-identical (JSON.stringify)
 * to the inline object SceneRow historically built — proven by
 * sceneJobInput.test.ts. The only addition is `noSnap`, appended AFTER the
 * legacy keys and ONLY when the boundary-adjust flow pinned the start, so
 * unadjusted candidates serialize exactly as before.
 *
 * Kept dependency-free (structural types only — no import from api.ts) so the
 * node-side vitest run can import it without dragging in browser-flavored
 * modules.
 */

/** The BacklogEntry fields the job payload is built from (structural subset). */
export interface SceneJobEntryFields {
  url?: string;
  from?: string;
  to?: string;
  startPhrase?: string;
  endPhrase?: string;
  host?: string;
  hostSource?: "operator" | "scan";
  guests?: string[];
  topic?: string;
  hookEn?: string;
  /** Set by the boundary-adjust flow when the start was seconds-nudged. */
  noSnap?: boolean;
}

export interface SceneJobInput {
  url: string;
  from: string;
  to?: string;
  startPhrase?: string;
  endPhrase?: string;
  host?: string;
  hostSource?: "operator" | "scan";
  guests?: string[];
  topic?: string;
  musicTrack?: string;
  autoScan: true;
  noSnap?: true;
}

/**
 * Build the createJob payload for a scene candidate.
 *
 * Semantics preserved verbatim from the historical inline object:
 *   - the candidate's phrases snap exactly in clip.ts;
 *   - `to` is the phrase-scan ceiling AND the fallback when the endPhrase
 *     misses (the 55s-scene-rendered-as-13s fix, 2026-07-09);
 *   - `musicChoice.trim() || undefined` keeps "" (Auto) out of the payload;
 *   - `autoScan: true` routes roster-aware speaker identification.
 * New: `noSnap` rides along ONLY when the boundary-adjust flow set it —
 * appended last so unadjusted payloads keep the exact legacy key order.
 */
export function buildSceneJobInput(entry: SceneJobEntryFields, musicChoice: string): SceneJobInput {
  return {
    url: entry.url!,
    from: entry.from ?? "0:00",
    to: entry.to,
    startPhrase: entry.startPhrase,
    endPhrase: entry.endPhrase,
    host: entry.host,
    hostSource: entry.hostSource,
    guests: entry.guests,
    topic: entry.topic ?? entry.hookEn,
    musicTrack: musicChoice.trim() || undefined,
    autoScan: true,
    ...(entry.noSnap ? { noSnap: true as const } : {}),
  };
}
